import React, { useState, useEffect } from 'react';
import { Server, Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from '../lib/i18n';
import { hostingPricesAPI, serversAPI, settingsAPI } from '../lib/api';

const HostingPrices = () => {
  const { t } = useTranslation();
  const [hostingPrices, setHostingPrices] = useState([]);
  const [hostingPackages, setHostingPackages] = useState([]); // Sunucular listesi
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 1,
    GBP: 1,
    GOLD: 1
  });
  const [formData, setFormData] = useState({
    hosting_name: '',
    selected_hosting_id: '',
    purchase_price: '',
    sale_price: '',
    purchase_currency: 'TRY',
    sale_currency: 'TRY'
  });

  useEffect(() => {
    fetchData();
    fetchExchangeRates();
    fetchHostingPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // hostingPackages yüklendikten sonra editingPrice varsa selected_hosting_id'yi güncelle
  useEffect(() => {
    if (editingPrice && hostingPackages.length > 0 && showEditForm) {
      
      let matchingServer = null;
      
      if (editingPrice.hosting_name) {
        const hostingName = editingPrice.hosting_name.trim();
        const hostingNameLower = hostingName.toLowerCase();
        
        // Tüm sunucuları logla
        hostingPackages.forEach(server => {
        });
        
        // Tam eşleşme
        matchingServer = hostingPackages.find(s => s.name === hostingName);
        
        // Tam eşleşme yoksa, kısmi eşleşme - hosting_name içinde sunucu adı var mı?
        if (!matchingServer) {
          matchingServer = hostingPackages.find(s => {
            const serverNameLower = s.name.toLowerCase();
            const result = hostingNameLower.includes(serverNameLower);
            return result;
          });
        }
        
        // Hala bulunamadıysa, tersine kontrol - sunucu adı içinde hosting_name'in bir kısmı var mı?
        if (!matchingServer) {
          matchingServer = hostingPackages.find(s => {
            const serverNameLower = s.name.toLowerCase();
            // "Kurumsal 100 | Synex" gibi durumlarda "|" işaretinden sonraki kısmı kontrol et
            const parts = hostingNameLower.split('|').map(p => p.trim());
            const result = parts.some(part => serverNameLower.includes(part)) || 
                          serverNameLower.includes(hostingNameLower.split('|')[0].trim());
            return result;
          });
        }
        
        // Son çare: "|" işaretinden sonraki kısmı al ve eşleştir
        if (!matchingServer && hostingName.includes('|')) {
          const afterPipe = hostingName.split('|')[1]?.trim();
          if (afterPipe) {
            matchingServer = hostingPackages.find(s => 
              s.name.toLowerCase().includes(afterPipe.toLowerCase()) ||
              afterPipe.toLowerCase().includes(s.name.toLowerCase())
            );
          }
        }
      }
      
      
      if (matchingServer) {
        setFormData(prev => {
          // Eğer zaten doğru değer set edilmişse güncelleme yapma
          if (prev.selected_hosting_id === matchingServer.id.toString()) {
            return prev;
          }
          return {
            ...prev,
            selected_hosting_id: matchingServer.id.toString()
          };
        });
      } else {
      }
    }
  }, [hostingPackages, editingPrice, showEditForm]);

  const fetchHostingPackages = async () => {
    try {
      const servers = await serversAPI.getAll();
      setHostingPackages(servers);
    } catch (error) {
      console.error('Servers fetch error:', error);
      toast.error(t('hostingPrices.serversLoadError') + ': ' + (error.message || t('common.unknownError')));
    }
  };

  const fetchExchangeRates = async () => {
    try {
      const settings = await settingsAPI.getAll();
      setExchangeRates({
        USD: parseFloat(settings.exchange_rate_usd) || 1,
        EUR: parseFloat(settings.exchange_rate_eur) || 1,
        GBP: parseFloat(settings.exchange_rate_gbp) || 1,
        GOLD: parseFloat(settings.exchange_rate_gold) || 1
      });
    } catch (error) {
      console.error('Exchange rates fetch error:', error);
    }
  };

  // Kar hesaplama fonksiyonu (TL cinsinden)
  const calculateProfit = (purchasePrice, purchaseCurrency, salePrice, saleCurrency) => {
    let purchaseInTRY = parseFloat(purchasePrice) || 0;
    let saleInTRY = parseFloat(salePrice) || 0;

    // Alış fiyatını TL'ye çevir
    if (purchaseCurrency !== 'TRY') {
      const rate = exchangeRates[purchaseCurrency] || 1;
      purchaseInTRY = purchaseInTRY * rate;
    }

    // Satış fiyatını TL'ye çevir
    if (saleCurrency !== 'TRY') {
      const rate = exchangeRates[saleCurrency] || 1;
      saleInTRY = saleInTRY * rate;
    }

    return saleInTRY - purchaseInTRY;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await hostingPricesAPI.getAll();
      setHostingPrices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Data fetch error:', error);
      toast.error(t('hostingPrices.fetchError') + ': ' + (error.message || t('common.unknownError')));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Hosting seçildiğinde paket adını otomatik doldur
    // Ama paket adı manuel değiştirildiğinde hosting seçimi etkilenmemeli
    if (name === 'selected_hosting_id') {
      const selectedServer = hostingPackages.find(s => s.id === parseInt(value));
      if (selectedServer && value) {
        // Hosting seçildiğinde paket adını otomatik doldur
        setFormData(prev => ({
          ...prev,
          selected_hosting_id: value,
          hosting_name: selectedServer.name || prev.hosting_name
        }));
        return;
      } else if (!value) {
        // Hosting seçimi temizlendiğinde sadece selected_hosting_id'yi temizle
        setFormData(prev => ({
          ...prev,
          selected_hosting_id: ''
        }));
        return;
      }
    }
    
    // hosting_name değiştirildiğinde selected_hosting_id etkilenmemeli
    // İkisi bağımsız çalışmalı - sadece hosting seçildiğinde paket adı otomatik doldurulur
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Fiyat değerlerini number'a çevir
      const submitData = {
        ...formData,
        purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : 0,
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : 0
      };

      if (editingPrice) {
        await hostingPricesAPI.update(editingPrice.id, submitData);
        toast.success(t('hostingPrices.updateSuccess'));
      } else {
        await hostingPricesAPI.create(submitData);
        toast.success(t('hostingPrices.addSuccess'));
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Hosting fiyatı işlem hatası:', error);
      toast.error(error.message || (editingPrice ? t('hostingPrices.updateError') : t('hostingPrices.addError')));
    }
  };

  const handleEdit = (price) => {
    
    setEditingPrice(price);
    
    // Hosting adına göre eşleşen sunucuyu bul
    let matchingServer = null;
    
    if (price.hosting_name && hostingPackages.length > 0) {
      const hostingName = price.hosting_name.trim();
      const hostingNameLower = hostingName.toLowerCase();
      
      // Tüm sunucuları logla
      hostingPackages.forEach(server => {
      });
      
      // Tam eşleşme
      matchingServer = hostingPackages.find(s => s.name === hostingName);
      
      // Tam eşleşme yoksa, kısmi eşleşme - hosting_name içinde sunucu adı var mı?
      if (!matchingServer) {
        matchingServer = hostingPackages.find(s => {
          const serverNameLower = s.name.toLowerCase();
          const result = hostingNameLower.includes(serverNameLower);
          return result;
        });
      }
      
      // Hala bulunamadıysa, tersine kontrol - sunucu adı içinde hosting_name'in bir kısmı var mı?
      if (!matchingServer) {
        matchingServer = hostingPackages.find(s => {
          const serverNameLower = s.name.toLowerCase();
          // "Kurumsal 100 | Synex" gibi durumlarda "|" işaretinden sonraki kısmı kontrol et
          const parts = hostingNameLower.split('|').map(p => p.trim());
          const result = parts.some(part => serverNameLower.includes(part)) || 
                        serverNameLower.includes(hostingNameLower.split('|')[0].trim());
          return result;
        });
      }
      
      // Son çare: "|" işaretinden sonraki kısmı al ve eşleştir
      if (!matchingServer && hostingName.includes('|')) {
        const afterPipe = hostingName.split('|')[1]?.trim();
        if (afterPipe) {
          matchingServer = hostingPackages.find(s => 
            s.name.toLowerCase().includes(afterPipe.toLowerCase()) ||
            afterPipe.toLowerCase().includes(s.name.toLowerCase())
          );
        }
      }
    }
    
    
    setFormData({
      hosting_name: price.hosting_name || '',
      selected_hosting_id: matchingServer ? matchingServer.id.toString() : '',
      purchase_price: price.purchase_price || '',
      sale_price: price.sale_price || '',
      purchase_currency: price.purchase_currency || 'TRY',
      sale_currency: price.sale_currency || 'TRY'
    });
    setShowAddForm(false);
    setShowEditForm(true);
    // Form açıldığında sayfanın en üstüne kaydır
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (priceId) => {
    if (!window.confirm(t('hostingPrices.deleteConfirm'))) {
      return;
    }

    try {
      await hostingPricesAPI.delete(priceId);
      toast.success(t('hostingPrices.deleteSuccess'));
      fetchData();
    } catch (error) {
      console.error('Hosting fiyatı silme hatası:', error);
      toast.error(error.message || t('hostingPrices.deleteError'));
    }
  };

  const handleToggleActive = async (price) => {
    try {
      await hostingPricesAPI.update(price.id, {
        is_active: !price.is_active
      });
      toast.success(t('hostingPrices.statusUpdateSuccess'));
      fetchData();
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast.error(error.message || t('hostingPrices.statusUpdateError'));
    }
  };

  const resetForm = () => {
    setFormData({
      hosting_name: '',
      selected_hosting_id: '',
      purchase_price: '',
      sale_price: '',
      purchase_currency: 'TRY',
      sale_currency: 'TRY'
    });
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingPrice(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-10 p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('hostingPrices.title')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 text-lg">{t('hostingPrices.subtitle')}</p>
          </div>
          <button 
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 shadow-sm hover:shadow-md transition-all duration-200"
          >
            <Plus className="h-5 w-5" />
            <span className="font-medium">{t('hostingPrices.addNew')}</span>
          </button>
        </div>
      </div>

      {/* Bilgi Notu */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-400 p-4 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
              {t('hostingPrices.infoText')}
            </p>
          </div>
        </div>
      </div>

      {/* Fiyat Ekleme Formu */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('hostingPrices.addTitle')}</h3>
            <button
              onClick={resetForm}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hostingPrices.selectHosting')}
                </label>
                <select
                  name="selected_hosting_id"
                  value={formData.selected_hosting_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('hostingPrices.manualEntry')}</option>
                  {hostingPackages.length > 0 ? (
                    hostingPackages.map(server => (
                      <option key={server.id} value={server.id}>
                        {server.name} {server.provider ? `(${server.provider})` : ''} {server.plan ? `- ${server.plan}` : ''}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>{t('hostingPrices.noServers')}</option>
                  )}
                </select>
                {hostingPackages.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('hostingPrices.noServersHint')}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hostingPrices.packageName')} *
                </label>
                <input
                  type="text"
                  name="hosting_name"
                  value={formData.hosting_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('hostingPrices.packageNamePlaceholder')}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('hostingPrices.autoFillHint')}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hostingPrices.purchasePrice')} *
                </label>
                <div className="flex">
                  <select
                    name="purchase_currency"
                    value={formData.purchase_currency}
                    onChange={handleInputChange}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="TRY">₺</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                    <option value="GBP">£</option>
                    <option value="GOLD">{t('hostingPrices.gold')}</option>
                  </select>
                  <input
                    type="number"
                    name="purchase_price"
                    value={formData.purchase_price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hostingPrices.salePrice')} *
                </label>
                <div className="flex">
                  <select
                    name="sale_currency"
                    value={formData.sale_currency}
                    onChange={handleInputChange}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="TRY">₺</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                    <option value="GBP">£</option>
                    <option value="GOLD">{t('hostingPrices.gold')}</option>
                  </select>
                  <input
                    type="number"
                    name="sale_price"
                    value={formData.sale_price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('hostingPrices.addPrice')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Fiyat Düzenleme Formu */}
      {showEditForm && editingPrice && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('hostingPrices.editTitle')}</h3>
            <button
              onClick={resetForm}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hostingPrices.selectHosting')}
                </label>
                <select
                  name="selected_hosting_id"
                  value={formData.selected_hosting_id}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('hostingPrices.manualEntry')}</option>
                  {hostingPackages.length > 0 ? (
                    hostingPackages.map(server => (
                      <option key={server.id} value={server.id}>
                        {server.name} {server.provider ? `(${server.provider})` : ''} {server.plan ? `- ${server.plan}` : ''}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>{t('hostingPrices.noServers')}</option>
                  )}
                </select>
                {hostingPackages.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {t('hostingPrices.noServersHint')}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hostingPrices.packageName')} *
                </label>
                <input
                  type="text"
                  name="hosting_name"
                  value={formData.hosting_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('hostingPrices.autoFillHint')}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hostingPrices.purchasePrice')} *
                </label>
                <div className="flex">
                  <select
                    name="purchase_currency"
                    value={formData.purchase_currency}
                    onChange={handleInputChange}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="TRY">₺</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                    <option value="GBP">£</option>
                    <option value="GOLD">{t('hostingPrices.gold')}</option>
                  </select>
                  <input
                    type="number"
                    name="purchase_price"
                    value={formData.purchase_price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('hostingPrices.salePrice')} *
                </label>
                <div className="flex">
                  <select
                    name="sale_currency"
                    value={formData.sale_currency}
                    onChange={handleInputChange}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="TRY">₺</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                    <option value="GBP">£</option>
                    <option value="GOLD">{t('hostingPrices.gold')}</option>
                  </select>
                  <input
                    type="number"
                    name="sale_price"
                    value={formData.sale_price}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
              >
                {t('common.update')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Fiyat Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('hostingPrices.listTitle')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('hostingPrices.packageName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('hostingPrices.purchasePrice')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('hostingPrices.salePrice')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('hostingPrices.profit')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('common.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {hostingPrices.map((price) => (
                <tr key={price.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Server className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{price.hosting_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {price.purchase_currency || 'TRY'} {parseFloat(price.purchase_price || 0).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {price.sale_currency || 'TRY'} {parseFloat(price.sale_price || 0).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-semibold ${calculateProfit(price.purchase_price, price.purchase_currency || 'TRY', price.sale_price, price.sale_currency || 'TRY') >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      ₺ {calculateProfit(price.purchase_price, price.purchase_currency || 'TRY', price.sale_price, price.sale_currency || 'TRY').toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(price)}
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        price.is_active 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {price.is_active ? t('common.active') : t('common.passive')}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEdit(price)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors duration-200"
                        title={t('common.edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(price.id)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-200"
                        title={t('common.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hostingPrices.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <Server className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{t('hostingPrices.noPrices')}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('hostingPrices.noPricesHint')}</p>
        </div>
      )}
    </div>
  );
};

export default HostingPrices;

